/* eslint-disable no-nested-ternary */
/* eslint-disable no-console */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { TableList, Container, Filter, Status } from './styles';

import Table, { IColumn } from '~/components/Table';
import FiltersLists from '~/components/FiltersLists';
import ExportCsv from '~/components/ExportCsv';
import api from '~/services/api';
import { formatPrice } from '~/utils/format';
import Loading from '~/components/Loading';
import ExportXls from '~/components/ExportXlsx';

interface IDataItem {
  id: string | number;
  organizer?: string;
  event: string;
  date: string;
  tickets_qty: number;
  total: string | number;
  payment: string;
  parcels: number;
  status_payment: string;
  status_event?: string;
  to_receive?: string;
  active?: boolean;
}

interface IItem {
  id: number;
}

interface IOrderData {
  courtesy_id: any;
  voucher: {
    code: string;
  };
  discount: string;
  id: number;
  total: string;
  code: string;
  status: string;
  created_at: string;
  items: IItem[];
  event: {
    name: string;
    date: string;
    user: {
      name: string;
    };
    status: {
      name: string;
    };
    organizer: {
      user: {
        name: string;
      };
    };
  };
  payment: {
    payment_type: string;
    organizer: string;
    status: string;
    installment: number;
  };
  user: {
    email: string;
    name: string;
  };
}

interface IOrderResponse {
  data: IOrderData[];
  from: number;
  to: number;
  total: number;
  pages: number;
}

const Payments: React.FC = () => {
  const [pageSelected, setPageSelected] = useState(1);
  const [data, setData] = useState<IDataItem[]>([]);
  const [tableData, setTableData] = useState({
    from: 0,
    to: 0,
    total: 0,
    totalPage: 0,
    generalTotal: 0,
  });
  const [userName, setUserName] = useState<string | undefined>(undefined);
  const [eventName, setEventName] = useState<string | undefined>(undefined);
  const [eventId, setEventId] = useState<string | undefined>(undefined);
  const [voucherCode, setVoucherCode] = useState<string | undefined>(undefined);
  const [orderFilters, setOrderFilters] = useState<string | undefined>();
  const [statusSelected, setStatusSelected] = useState<string | undefined>(
    undefined
  );
  const [loading, setLoading] = useState(false);
  const [quantity, setQuantity] = useState(10);

  const status = useMemo(
    () => [
      {
        id: 'Pago',
        value: 'Pago',
        selected: true,
      },
      {
        id: 'Cortesia',
        value: 'Cortesia',
        selected: true,
      },
      {
        id: 'Aguardando pagamento',
        value: 'Aguardando pagamento',
        selected: false,
      },
      {
        id: 'Cancelado',
        value: 'Cancelado',
        selected: false,
      },
    ],
    []
  );

  const handleLoadOrders = useCallback(
    async (
      pageData,
      user?: string,
      event?: string,
      statusData?: string,
      quantityData?: number,
      event_id?: string,
      voucher?: string,
      orderFilter?: string
    ) => {
      setLoading(true);
      const response = await api
        .get<IOrderResponse>('orders/organizers', {
          params: {
            page: pageData,
            user,
            event,
            status: statusData,
            quantity: quantityData,
            event_id: event_id === '-1' ? undefined : event_id,
            voucher,
            order_id: orderFilter?.slice(12) || orderFilter,
          },
        })
        .finally(() => {
          setLoading(false);
        });

      const dataAux = response.data.data.map<IDataItem>((order) => {
        const to_receive =
          parseFloat(order.total) - parseFloat(order.total) * 0.03;

        return {
          id: order.id,
          organizer: order.event.user?.name,
          event: order.event.name,
          buyer_name: order.user.name,
          email: order.user.email,
          date: format(parseISO(order.created_at), 'dd/MM/yyyy'),
          tickets_qty: order.items.length,
          total: formatPrice(
            parseFloat(
              order.payment
                ? order.payment.payment_type.toLowerCase() === 'free'
                  ? '0'
                  : order.payment.organizer
                : '0'
            )
          ),
          voucher: order.voucher?.code ?? '-',
          discount:
            order.discount === '0.00'
              ? '-'
              : formatPrice(parseFloat(order.discount)),
          payment: order.courtesy_id
            ? 'Cortesia'
            : order.payment?.payment_type.toLowerCase().includes('credit')
            ? 'Cartão de Crédito'
            : order.payment?.payment_type.toLowerCase() === 'free'
            ? 'Grátis'
            : 'PIX',
          parcels: order.payment?.installment ?? 1,
          status_payment: order.status,
          status_event: order.event.status.name,
          to_receive: formatPrice(to_receive),
          code: order.code,
          active: true,
        };
      });

      setData(dataAux);
      setTableData((oldState) => ({
        from: response.data.from,
        to: response.data.to,
        total: response.data.total,
        totalPage: response.data.pages,
        generalTotal: oldState.generalTotal
          ? oldState.generalTotal
          : response.data.total,
      }));
    },
    []
  );

  useEffect(() => {
    handleLoadOrders(1);
  }, [handleLoadOrders]);

  const columns = useMemo<IColumn[]>(
    () => [
      {
        name: 'Pedido',
        selector: 'id',
        sortable: true,
        cell: (row) => <span>{row.code}</span>,
      },

      {
        name: 'Evento',
        selector: 'event',
        sortable: true,
      },
      {
        name: 'Nome comprador',
        selector: 'buyer_name',
        sortable: true,
      },
      {
        name: 'Email',
        selector: 'email',
        sortable: true,
      },
      {
        name: 'Data Venda',
        selector: 'date',
        sortable: true,
      },
      {
        name: 'Inscrições',
        selector: 'tickets_qty',
        sortable: true,
      },
      {
        name: 'Total',
        selector: 'total',
        sortable: true,
      },
      {
        name: 'Cupom',
        selector: 'voucher',
        sortable: true,
      },
      {
        name: 'Desconto',
        selector: 'discount',
        sortable: true,
      },
      {
        name: 'Forma pgto',
        selector: 'payment',
        sortable: true,
      },
      {
        name: 'Parcelas',
        selector: 'parcels',
        sortable: true,
      },
      {
        name: 'Status pagto',
        selector: 'status_payment',
        sortable: true,
        cell: (row) => (
          <Status
            status={
              row.status_payment === 'Cortesia' ? 'Pago' : row.status_payment
            }
            className="text-white rounded-pill mb-0"
          >
            {row.status_payment === 'Cortesia'
              ? 'Cortesia'
              : row.status_payment}
          </Status>
        ),
      },
    ],
    []
  );

  const handleChangePage = useCallback(
    (page) => {
      if (page <= tableData.totalPage) {
        handleLoadOrders(
          page,
          userName,
          eventName,
          statusSelected,
          quantity,
          eventId,
          voucherCode,
          orderFilters
        );
        setPageSelected(page);
      }
    },
    [
      tableData.totalPage,
      handleLoadOrders,
      userName,
      eventName,
      statusSelected,
      quantity,
      eventId,
      voucherCode,
      orderFilters,
    ]
  );

  const handleSubmitFilters = useCallback(
    (formData) => {
      setUserName(formData.name);
      setEventName(formData.event === 'Todos' ? undefined : formData.event);
      setEventId(formData.event_id === 'Todos' ? undefined : formData.event_id);
      setStatusSelected(
        formData.statusPayment !== 'todos' ? formData.statusPayment : undefined
      );
      setVoucherCode(formData.codeVoucher || undefined);
      setOrderFilters(formData.order || undefined);
      handleLoadOrders(
        1,
        formData.name,
        formData.event === 'Todos' ? undefined : formData.event,
        formData.statusPayment !== 'todos' ? formData.statusPayment : undefined,
        quantity,
        formData.event === 'Todos' ? undefined : formData.event_id,
        formData.codeVoucher || undefined,
        formData.order || undefined
      );
      setPageSelected(1);
    },
    [handleLoadOrders, quantity]
  );

  const handleChangeQuantity = useCallback(
    (quantityData) => {
      handleLoadOrders(
        1,
        userName,
        eventName,
        statusSelected,
        quantityData,
        eventId,
        voucherCode,
        orderFilters
      );
      setQuantity(quantityData);
      setPageSelected(1);
    },
    [
      eventName,
      handleLoadOrders,
      statusSelected,
      userName,
      eventId,
      voucherCode,
      orderFilters,
    ]
  );

  const handleClickExportCsv = useCallback(async () => {
    setLoading(true);
    const response = await api
      .get<IOrderData[]>('orders/organizers', {
        params: {
          user: userName,
          event: eventName,
          event_id: eventId === '-1' ? undefined : eventId,
          status: statusSelected,
          voucher: voucherCode,
          order_id: orderFilters?.slice(12) || orderFilters,
        },
      })
      .finally(() => setLoading(false));

    const dataAux = response.data.map<IDataItem>((order) => {
      const to_receive =
        parseFloat(order.total) - parseFloat(order.total) * 0.03;

      return {
        id: `#${order.code}`,
        // organizer: order.event.user?.name,
        event: order.event.name,
        buyer_name: order.user.name,
        email: order.user.email,
        date: format(parseISO(order.created_at), 'dd/MM/yyyy'),
        tickets_qty: order.items.length,
        total: parseFloat(
          order.payment
            ? order.payment.payment_type.toLowerCase() === 'free'
              ? '0'
              : order.payment.organizer
            : '0'
        ),
        voucher: order.voucher?.code ?? '-',
        discount: order.discount === '0.00' ? '-' : parseFloat(order.discount),
        payment: order.courtesy_id
          ? 'Cortesia'
          : order.payment?.payment_type.toLowerCase().includes('credit')
          ? 'Cartão de Crédito'
          : order.payment?.payment_type.toLowerCase() === 'free'
          ? 'Grátis'
          : 'PIX',
        parcels: order.payment?.installment ?? 1,
        status_payment: order.status,
        // status_event: order.event.status.name,
        // to_receive: formatPrice(to_receive),
        // active: true,
      };
    });

    return dataAux;
  }, [eventId, eventName, statusSelected, userName, voucherCode, orderFilters]);

  return (
    <Container>
      <div className="container-fluid">
        <div className="row">
          <div className="zoom col-12 px-0">
            <div className="bg-white d-sm-flex justify-content-between align-items-center p-4">
              <h1 className="mb-0">Lista de pedidos</h1>
              <div>
                <ExportCsv
                  data={data}
                  columns={columns}
                  fileName="Lista_de_pedidos"
                  onClick={handleClickExportCsv}
                />
                <ExportXls
                  data={data}
                  columns={columns}
                  fileName="Lista_de_pedidos"
                  onClick={handleClickExportCsv}
                  className="export-xls"
                />
              </div>
            </div>
          </div>
          <Filter className="col-12 px-0 mt-3">
            <FiltersLists
              onSubmitFilters={handleSubmitFilters}
              event
              codeVoucher
              name
              order
              statusPayment
              statusOptions={status}
            />
          </Filter>
          <div className="col-12 px-0">
            <div className="row">
              <TableList className="col-12 mt-3">
                <div className="bg-white pb-2">
                  <div className="bg-white overflow-auto">
                    <div className="">
                      <Table
                        className="mb-0 p-0 table"
                        columns={columns}
                        data={data}
                        pagination
                        changeQuantity
                        showTotals
                        generalTotal={tableData.generalTotal}
                        totalData={tableData.total}
                        selectedPage={pageSelected}
                        onChangePage={handleChangePage}
                        onChangeQuantity={handleChangeQuantity}
                      />
                    </div>
                  </div>
                </div>
              </TableList>
            </div>
          </div>
        </div>
      </div>
      <Loading active={loading} text="Carregando..." />
    </Container>
  );
};

export default Payments;
